import { Block } from '../types/Block';
import { BlockCalculationService } from '../services/BlockCalculationService';

export type DeltaSeverity = 'none' | 'gray' | 'orange' | 'red';

export interface LotBudgetInfo {
  current: number;
  initial?: number;
  delta: number;
  deltaPct: number;
  severity: DeltaSeverity;
  hasInitial: boolean;
}

export function computeLotCurrentTotal(blocks: Block[], lotId: string): number {
  const childrenByParent = new Map<string, string[]>();
  for (const b of blocks) {
    if (b.parentId) {
      const arr = childrenByParent.get(b.parentId) ?? [];
      arr.push(b.id);
      childrenByParent.set(b.parentId, arr);
    }
  }
  const byId = new Map(blocks.map(b => [b.id, b]));
  const descendants: Block[] = [];
  const frontier: string[] = [...(childrenByParent.get(lotId) ?? [])];
  while (frontier.length) {
    const id = frontier.shift()!;
    const b = byId.get(id);
    if (!b) continue;
    descendants.push(b);
    const kids = childrenByParent.get(id);
    if (kids) frontier.push(...kids);
  }
  return BlockCalculationService.calculateTotal(descendants);
}

export function computeBudgetInfo(current: number, initial: number | undefined): LotBudgetInfo {
  const hasInitial = typeof initial === 'number' && initial > 0;
  if (!hasInitial) {
    return { current, initial, delta: 0, deltaPct: 0, severity: 'none', hasInitial: false };
  }
  const delta = current - (initial as number);
  const deltaPct = (delta / (initial as number)) * 100;
  const abs = Math.abs(deltaPct);
  let severity: DeltaSeverity;
  if (abs < 5) severity = 'gray';
  else if (abs < 15) severity = 'orange';
  else severity = 'red';
  return { current, initial, delta, deltaPct, severity, hasInitial: true };
}

export function formatDeltaPct(deltaPct: number): string {
  const sign = deltaPct >= 0 ? '+' : '';
  return `${sign}${deltaPct.toFixed(1).replace('.', ',')}%`;
}

export function formatDeltaAmount(delta: number): string {
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${BlockCalculationService.formatPrice(delta)} €`;
}

export function severityTextClass(severity: DeltaSeverity): string {
  switch (severity) {
    case 'gray': return 'text-gray-500';
    case 'orange': return 'text-orange-500';
    case 'red': return 'text-orange-700 font-semibold';
    default: return 'text-gray-400';
  }
}
